import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Input, message, Modal, Select, Space, Table } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import PlotIconGroup from "./PlotIconGroup";
import { LoadingIcon } from "../../../components";
import {
  createPlot,
  deletePlot,
  getCrops,
  getPlots,
  getVarieties,
  updatePlot,
} from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import CreatePlotModalContent from "./CreatePlotModalContent";

const PlotTable = ({
  plotLabel,
  currentOrg,
  setCurrentOrg,
  orgData,
  currentSite,
  setCurrentSite,
  siteData,
  currentCrop,
  setCurrentCrop,
  cropData,
}) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const { userData } = useContext(UserContext);

  // State for Create Modal
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

  const [allCrops, setAllCrops] = useState([]);
  const [allVarieties, setAllVarieties] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const renderBoolean = (bool) => (
    <p className={`${bool ? "text-primaryBlue" : "text-dangerRed"}`}>
      {String(bool)}
    </p>
  );

  const fetchPlots = async () => {
    try {
      setDataLoading(true);
      const res = await getPlots(currentOrg, currentSite, currentCrop);
      const fetchedData = res.data?.data?.map((plot) => ({
        ...plot,
        key: plot.id,
        site_display_name: plot.site?.display_name || plot.site?.name,
        crop_display_name: plot.crop?.display_name || plot.crop?.name,
        variety_name: plot.variety?.name || "-",
      }));
      setData(fetchedData);
    } catch (err) {
      message.error("Error fetching Plots!");
    } finally {
      setDataLoading(false);
    }
  };

  const createPlotHandler = async (data) => {
    try {
      const final_data = {
        organization: data.organization,
        site: data.site,
        crop: data.crop,
        name: data.name,
        variety: data.variety,
        is_active: data.is_active,
        is_loose: data.is_loose || false,
        metadata: data.metadata,
      };
      await createPlot(final_data);
      message.success(plotLabel + " created successfully!");
      setIsCreateModalVisible(false);
      fetchPlots();
    } catch (err) {
      message.error("Error creating " + plotLabel + "!");
    }
  };

  const updatePlotHandler = async (id, data) => {
    try {
      const final_data = {
        organization: data.organization,
        site: data.site,
        crop: data.crop,
        name: data.name,
        variety: data.variety,
        is_active: data.is_active,
        is_loose: data.is_loose,
        metadata: data.metadata,
      };
      await updatePlot(id, final_data);
      message.success(plotLabel + " updated successfully!");
      fetchPlots();
    } catch (err) {
      message.error("Error updating " + plotLabel + "!");
    }
  };

  const deletePlotHandler = async (id) => {
    try {
      await deletePlot(id);
      message.success(plotLabel + " deleted successfully!");
      fetchPlots();
    } catch (err) {
      message.error("Error deleting " + plotLabel + "!");
    }
  };

  const fetchAllCrops = async () => {
    try {
      const res = await getCrops(currentSite);
      const cropsData = res.data.data
        .filter((crop) => crop.is_active)
        .map((crop) => ({
          value: crop.id,
          label: crop.display_name,
        }));
      setAllCrops(cropsData);
    } catch (err) {
      message.error("Error fetching Crops!");
    }
  };

  const fetchAllVariety = async () => {
    try {
      setDataLoading(true);
      const res = await getVarieties(currentOrg, currentSite, currentCrop);
      const fetchedData = res.data?.data?.map((variety) => ({
        ...variety,
        key: variety.id,
      }));
      setAllVarieties(fetchedData);
    } catch (err) {
      message.error("Error fetching Variety!");
    }
  };

  // Fetch Plots and Crops on Org or Site Change
  useEffect(() => {
    if (currentOrg && currentSite) {
      fetchPlots();
      fetchAllCrops();
      fetchAllVariety();
    }
  }, [currentOrg, currentSite, currentCrop]);

  // Search Handlers
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#39f" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            ?.toLowerCase()
            ?.includes(value?.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span>
          {text
            .toString()
            .split(new RegExp(`(${searchText})`, "gi"))
            .map((fragment, i) =>
              fragment?.toLowerCase() === searchText?.toLowerCase() ? (
                <span key={i} className="highlight">
                  {fragment}
                </span>
              ) : (
                fragment
              ),
            )}
        </span>
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Site",
      dataIndex: "site_display_name",
      key: "site",
      ...getColumnSearchProps("site_display_name"),
    },
    {
      title: "Crop",
      dataIndex: "crop_display_name",
      key: "crop",
      ...getColumnSearchProps("crop_display_name"),
    },
    {
      title: "Variety",
      dataIndex: "variety_name",
      key: "variety",
      ...getColumnSearchProps("variety_name"),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: "Loose Harvested",
      dataIndex: "is_loose",
      key: "is_loose",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
      filters: [
        { text: "Loose", value: true },
        { text: "Not Loose", value: false },
      ],
      onFilter: (value, record) => record.is_loose === value,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <Space size="middle">
          <PlotIconGroup
            plotLabel={plotLabel}
            plot={record}
            updateCallback={updatePlotHandler}
            deleteCallback={deletePlotHandler}
            allOrgs={orgData.map((org) => ({
              value: org.id,
              label: org.name,
            }))}
            allSites={siteData.map((site) => ({
              value: site.id,
              label: site.display_name,
            }))}
            allCrops={cropData.map((crop) => ({
              value: crop.id,
              label: crop.display_name,
            }))}
            allVarieties={allVarieties.map((variety) => ({
                value: variety.id,
                label: variety.name,
              }))}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname="absolute h-full top-[40vh]" height="200px" />
      ) : (
        <>
          <div
            className={`w-full pb-10 h-fit flex justify-between items-center`}
          >
            <div className="flex justify-start items-center">
              {userData?.is_superuser && (
                <Select
                  className={`min-w-[150px] mr-5`}
                  placeholder="Select Organization"
                  value={currentOrg}
                  onChange={setCurrentOrg}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={orgData?.map((org) => ({
                    value: org.key,
                    label: org.name,
                  }))}
                />
              )}
              <Select
                className={`min-w-[150px] mr-5`}
                placeholder="Select Site"
                value={currentSite}
                onChange={setCurrentSite}
                showSearch
                filterOption={(input, option) =>
                  (option?.label || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={siteData?.map((site) => ({
                  value: site.id,
                  label: site.display_name,
                }))}
              />
              <Select
                className={`min-w-[150px]`}
                placeholder="Select Crop"
                disabled={!currentOrg || !currentSite}
                value={currentCrop}
                onChange={setCurrentCrop}
                showSearch
                filterOption={(input, option) =>
                  option.label?.toLowerCase().includes(input?.toLowerCase())
                }
                options={cropData?.map((crop) => ({
                  value: crop.id,
                  label: crop.display_name,
                }))}
              />
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
              disabled={allCrops.length === 0}
            >
              Create {plotLabel}
            </Button>
          </div>
          <Table
            dataSource={data}
            columns={columns}
            pagination={{
              hideOnSinglePage: true,
              position: ["bottomCenter"],
            }}
          />
          {/* Create Plot Modal */}
          <Modal
            title={"Create " + plotLabel}
            open={isCreateModalVisible}
            onCancel={() => setIsCreateModalVisible(false)}
            footer={null}
          >
            <CreatePlotModalContent
              plotLabel={plotLabel}
              setOpen={setIsCreateModalVisible}
              createCallback={createPlotHandler}
              allOrgs={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
              allSites={siteData.map((site) => ({
                value: site.id,
                label: site.display_name,
              }))}
              allCrops={cropData.map((crop) => ({
                value: crop.id,
                label: crop.display_name,
              }))}
              allVarieties={allVarieties.map((variety) => ({
                value: variety.id,
                label: variety.name,
              }))}
              currentOrg={currentOrg}
              currentSite={currentSite}
              currentCrop={currentCrop}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default PlotTable;
